import React, { useState, useEffect } from 'react';
import { X, UserX, Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, query, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import ConfirmDialog from './ConfirmDialog';
import '../styles/BlockedContactsModal.css';

const REASON_LABELS = {
  not_interested: 'Not Interested',
  unsubscribe: 'Unsubscribe Request',
  do_not_contact: 'Do Not Contact',
  no_tenant_reps: 'No Tenant Reps Policy',
  direct_only: 'Direct Deals Only',
  hostile: 'Negative Response'
};

export default function BlockedContactsModal({ isOpen, onClose }) {
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [removeError, setRemoveError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [contactToRemove, setContactToRemove] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setLoadError('Sign in to view blocked contacts.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');
    const blockedRef = collection(db, 'users', uid, 'optedOutContacts');
    const q = query(blockedRef);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const contacts = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        optedOutAt: d.data().optedOutAt?.toDate?.() || new Date()
      }));

      // Sort by most recent first
      contacts.sort((a, b) => b.optedOutAt - a.optedOutAt);

      setBlockedContacts(contacts);
      setLoadError('');
      setLoading(false);
    }, (err) => {
      console.error('Failed to load blocked contacts:', err);
      setBlockedContacts([]);
      setLoadError('Could not load blocked contacts. Refresh Settings or try again in a moment.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isOpen]);

  const handleRemove = async (contact) => {
    setRemoving(contact.id);
    setRemoveError('');
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      await deleteDoc(doc(db, 'users', uid, 'optedOutContacts', contact.id));
      setContactToRemove(null);
    } catch (err) {
      console.error('Failed to remove from blocklist:', err);
      setRemoveError('Could not remove this blocked contact. Try again in a moment.');
    } finally {
      setRemoving(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredContacts = normalizedSearchTerm
    ? blockedContacts.filter(contact => [
      contact.email,
      REASON_LABELS[contact.reason],
      contact.reason,
      contact.clientName,
      contact.propertyAddress,
      contact.threadSubject
    ].some(value => String(value || '').toLowerCase().includes(normalizedSearchTerm)))
    : blockedContacts;

  return (
    <>
    <div className="blocked-modal-overlay" onClick={onClose}>
      <div className="blocked-modal" role="dialog" aria-modal="true" aria-labelledby="blocked-contacts-title" onClick={(e) => e.stopPropagation()}>
        <div className="blocked-modal-header">
          <div className="blocked-modal-title">
            <UserX size={24} />
            <h2 id="blocked-contacts-title">Blocked Contacts</h2>
            <span className="blocked-count">{blockedContacts.length}</span>
          </div>
          <button className="blocked-modal-close" onClick={onClose} aria-label="Close blocked contacts">
            <X size={24} />
          </button>
        </div>

        <div className="blocked-modal-description">
          <AlertCircle size={16} />
          <span>These contacts have opted out and will not receive emails from future campaigns.</span>
        </div>

        <div className="blocked-modal-tools">
          <label>
            <span>Search</span>
            <input
              type="search"
              aria-label="Search blocked contacts"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search email, reason, campaign, or property"
            />
          </label>
        </div>

        {removeError && (
          <p className="blocked-modal-error" role="status">
            {removeError}
          </p>
        )}

        <div className="blocked-modal-content">
          {loading ? (
            <div className="blocked-loading">
              <RefreshCw className="spin" size={24} />
              <span>Loading blocked contacts...</span>
            </div>
          ) : loadError ? (
            <div className="blocked-empty blocked-empty--error">
              <AlertCircle size={48} />
              <h3>Could not load blocked contacts</h3>
              <p>{loadError}</p>
            </div>
          ) : blockedContacts.length === 0 ? (
            <div className="blocked-empty">
              <UserX size={48} />
              <h3>No Blocked Contacts</h3>
              <p>Contacts who opt out will appear here.</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="blocked-empty">
              <UserX size={48} />
              <h3>No Matching Blocked Contacts</h3>
              <p>Clear the search to see every blocked contact.</p>
            </div>
          ) : (
            <div className="blocked-list">
              {filteredContacts.map((contact) => (
                <div key={contact.id} className="blocked-item">
                  <div className="blocked-item-info">
                    <div className="blocked-item-email">{contact.email}</div>
                    {(contact.clientName || contact.propertyAddress || contact.threadSubject) && (
                      <div className="blocked-item-context">
                        {[contact.clientName, contact.propertyAddress, contact.threadSubject].filter(Boolean).join(' / ')}
                      </div>
                    )}
                    <div className="blocked-item-meta">
                      <span className={`blocked-reason blocked-reason--${contact.reason || 'unknown'}`}>
                        {REASON_LABELS[contact.reason] || contact.reason || 'Unknown'}
                      </span>
                      <span className="blocked-date">
                        Blocked on {formatDate(contact.optedOutAt)}
                      </span>
                    </div>
                  </div>
                  <button
                    className="blocked-item-remove"
                    onClick={() => setContactToRemove(contact)}
                    disabled={removing === contact.id}
                    title={`Remove ${contact.email} from blocklist`}
                    aria-label={`Remove ${contact.email} from blocklist`}
                  >
                    {removing === contact.id ? (
                      <RefreshCw className="spin" size={16} />
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="blocked-modal-footer">
          <button className="blocked-modal-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
    <ConfirmDialog
      open={Boolean(contactToRemove)}
      title="Remove Blocked Contact"
      description={contactToRemove ? `${contactToRemove.email} will receive emails in future campaigns if they appear on an outreach sheet.` : ''}
      confirmLabel="Remove"
      cancelLabel="Keep Blocked"
      variant="danger"
      isLoading={Boolean(removing)}
      onConfirm={() => contactToRemove && handleRemove(contactToRemove)}
      onCancel={() => setContactToRemove(null)}
    />
    </>
  );
}
